import { useState, useRef, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
//import { toast } from "react-toastify";

import { 
  Breadcrumbs, Box, Button,
  Chip,
  Collapse, Checkbox,
  FormControl,
  IconButton, InputAdornment, InputLabel, 
  MenuItem, Modal,
  OutlinedInput,
  Paper,
  Select, 
  Stack,
  TextField, Typography, 
  useMediaQuery,
  useTheme 
} from "@mui/material";
import styled from "styled-components";

import SyncIcon from "@mui/icons-material/Sync";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_KR } from "@ag-grid-community/locale";
//import 'ag-grid-community/styles/ag-grid.css'; // 필수 스타일 (ag grid v32 버전)
//import 'ag-grid-community/styles/ag-theme-alpine.css'; // 테마 스타일 (ag grid v32 버전)
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Theming API 전용 스타일(ag grid v33 버전)

import { 
  useGetTableData, 
  useCreateTableData, 
  usePutTableData,
  useDeleteTableData, 
} from "../../../components/Table/hooks";
//import { useSelectData } from "../../../hooks/useSelectData";
import { standardMachineColumns } from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { SnackbarAlert, ConfirmDialog, BreadcrumbsTable } from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1199px) {
    padding: 12px;
    overflow: auto;
  }
`;

// 커스텀 MenuItem 스타일
const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: `${FONT_PROPS.PRIMARY} !important`,
  lineHeight: `${FONT_PROPS} !important`,
  fontWeight: `${FONT_PROPS.MEDIUM} !important`, // 폰트 굵기 medium
  color: `${FONT_PROPS.DARK} !important`, // 기본 텍스트 검정
  backgroundColor: '#ffffff', // 기본 배경 흰색
  '&:hover': {
    backgroundColor: 'lightgray', // 마우스 올릴 때 연한 회색
  },
  '&.Mui-selected': {
    backgroundColor: 'white !important', // 선택된 항목도 흰색 유지
    color: 'black',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'lightgray !important', // 선택된 상태에서 호버 시 연한 회색 유지
  },
}));

///////////////////////////////
// 정보관리의 설비기준정보 컴포넌트
///////////////////////////////
const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "대상 기계/기구 정보";
export function StandardMachine() {
  const theme = useTheme();
  const isReadOnly = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const { group_code, project_code } = useParams();

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 마스터 데이터의 장비 조회 요청
  const getMasterMachineData = useGetTableData(
    "request",
    "/getMachineList",
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableData(
    "request",
    "/getStandardMachine", // 서버 요청 경로
    group_code,
    project_code
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postStandardMachine",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData( 
    "request",
    "/putStandardMachine",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteStandardMachine",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const [compactSelectedRowIds, setCompactSelectedRowIds] = useState({});
  const getSelectedRows = () => {
    if (isCompactLayout) {
      return getData.data
        .filter((row) => compactSelectedRowIds[String(row.machine_id)])
        .map((row) => ({ original: row }));
    }
    return tableRef.current?.getSelectedRows() || [];
  };
  const clearAllSelection = () => {
    clearSelection();
    setCompactSelectedRowIds({});
  };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const compactSelectedCount = Object.keys(compactSelectedRowIds).length;
  const formatDate = (value) => (dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "-");
  const getMachineDisplayName = (row) => {
    const masterMachineName = row?.machine_name;
    const safeMachineName = row?.safe_machine_name;
    if (safeMachineName && safeMachineName !== "") {
      return `${safeMachineName}${masterMachineName ? ` (${masterMachineName})` : ""}`;
    }
    return masterMachineName || "-";
  };
  const filteredCompactData = useMemo(() => {
    const keyword = globalFilter.trim().toLowerCase();
    if (!keyword) return getData.data;

    return getData.data.filter((row) => {
      const values = [
        row.machine_manage_code,
        getMachineDisplayName(row),
        row.machine_manufacturer,
        row.machine_model_name,
        row.machine_standard,
        row.machine_age,
        row.machine_size,
        row.machine_voltage,
        formatDate(row.machine_purchase_date),
      ];
      return values.some((value) => String(value ?? "").toLowerCase().includes(keyword));
    });
  }, [globalFilter, getData.data]);
  const toggleCompactRowSelection = (machineId) => {
    const key = String(machineId);
    setCompactSelectedRowIds((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>
        {!isReadOnly ? (
          <>
            {/* 동기화 버튼 */}
            <Buttons.Custom
              startIcon={<SyncIcon />}
              onClick={() => {
                openSyncModal(); // 동기화 모달 열기
              }}
              sx={{ padding: '12px 16px' }}
            >
              동기화
            </Buttons.Custom>
            {/* 삭제 버튼 */}
            <Buttons.Delete 
              onClick={openDeleteModal} // 행 삭제 로직 실행
              disabled={table.getSelectedRowModel().rows.length === 0}
              sx={{ padding: '12px 16px' }}
            />    
          </>
        ) : null}
      </Box>
  </Box>
  );

  // 설비기준정보 수정 모달 상태 관리
  const [showEditModal, setShowEditModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const openEditModal = (rowData) => {
    setShowEditModal({
      ...showEditModal,
      isOpen: true,
      rowData: rowData,
    });
  };
  const closeEditModal = () =>{
    setShowEditModal({
      ...showEditModal,
      isOpen: false,
      rowData: null,
    });
  };
  // 설비기준정보 테이블의 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Buttons.Edit
        onClick={(event) =>{
          event.stopPropagation(); // 이벤트 전파 중단
          openEditModal(rowData);
        }}
        sx={{ padding: '8px 12px' }}
      />
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    //console.log(updateData);
    try {
      const result = await putData.mutateAsync(updateData);
      if(result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }
      clearAllSelection();

      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }

  // 설비기준정보 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });
  // DB 업데이트 (삭제 데이터)
  const updateDeleteData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.machine_id);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'machine_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearAllSelection();
      
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };
  
  ////////////////////////////
  // 장비 동기화 관련 
  ////////////////////////////

  // 동기화 모달 상태 관리
  const [syncModal, setSyncModal]  = useState({ isOpen: false }); 
  const openSyncModal = () => {
    setSyncModal({
      ...syncModal,
      isOpen: true,
    });
  };
  const closeSyncModal = () => {
    setSyncModal({
      ...syncModal,
      isOpen: false,
    });
  };
  // DB 업데이트 (추가 데이터)
  const updateAddData = async (addData) => {
    try{
      //console.log("새로운 데이터: ", addData);

      const result = await postData.mutateAsync(addData);
      if(result.status === 200) {
        openDataAlert(message.updateDataSyncSuccess);
      }

      //handleSyncModalClose();
    }
    catch(error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    if (isCompactLayout) {
      return (
        <TableContainer>
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="설비명, 관리번호, 제작사 검색"
                  value={globalFilter}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setGlobalFilter("")}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  justifyContent={{ xs: "space-between", sm: "flex-end" }}
                  sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                >
                  <Buttons.Custom
                    startIcon={<SyncIcon />}
                    onClick={openSyncModal}
                    sx={{ minHeight: 38, px: 1.5 }}
                  >
                    동기화
                  </Buttons.Custom>
                  <Buttons.Delete
                    onClick={openDeleteModal}
                    disabled={compactSelectedCount === 0}
                    sx={{ minHeight: 38, px: 1.5 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1,
              }}
            >
              {filteredCompactData.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    gridColumn: "1 / -1",
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    color: "text.secondary",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography fontWeight={800}>표시할 설비가 없습니다.</Typography>
                </Paper>
              ) : (
                filteredCompactData.map((row) => {
                  const rowKey = String(row.machine_id);
                  const selected = Boolean(compactSelectedRowIds[rowKey]);
                  return (
                    <Paper
                      key={rowKey}
                      variant="outlined"
                      sx={{
                        p: { xs: 1.35, sm: 1.5 },
                        borderRadius: 2,
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: "background.paper",
                        boxShadow: selected ? "0 8px 20px rgba(99, 102, 241, 0.12)" : "none",
                        minWidth: 0,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => toggleCompactRowSelection(row.machine_id)}
                          sx={{ p: 0.25, mt: 0.15 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 0.5, minWidth: 0 }}
                          >
                            <Chip
                              size="small"
                              label={row.machine_manage_code || "관리번호 없음"}
                              sx={{ height: 24, fontWeight: 800, borderRadius: 1, maxWidth: "70%" }}
                            />
                            <Buttons.Edit
                              onClick={() => openEditModal(row)}
                              sx={{ minWidth: 0, px: 1.25, py: 0.65 }}
                            />
                          </Stack>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: "text.primary",
                              lineHeight: 1.35,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getMachineDisplayName(row)}
                          </Typography>
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "72px minmax(0, 1fr)",
                                gap: 0.75,
                                alignItems: "baseline",
                              }}
                            >
                              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
                                제작/모델
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {[row.machine_manufacturer, row.machine_model_name].filter(Boolean).join(" · ") || "-"}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
                                규격/Size
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {[row.machine_standard, row.machine_size].filter(Boolean).join(" · ") || "-"}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
                                구입/전압
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {[formatDate(row.machine_purchase_date), row.machine_voltage].filter((value) => value && value !== "-").join(" · ") || "-"}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })
              )}
            </Box>
          </Stack>

          {renderDialogs()}
        </TableContainer>
      );
    }

    return (
      <TableContainer>
        {/** 설비기준정보 테이블 표시 */}
        <NewTableTemplate 
          ref={tableRef}
          columns={standardMachineColumns}
          data={getData.data}
          topToolbarContent={customTableTopToolbar}
          renderRowActionContent={isReadOnly ? null : customRenderRowActionContent}
          enableCustomGlobalFilter={true}
          enabledSelectionAllRow={!isReadOnly}
          rowActionsColumnSize={96}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
        {renderDialogs()}
      </TableContainer>
    );
  }

  function renderDialogs() {
    return (
      <>
        {/* 장비 정보 동기화 모달 */}
        <SyncMachineListModal
          isOpen={syncModal.isOpen}
          onConfirm={updateAddData}
          onCancel={closeSyncModal}
          title="장비 정보 동기화"
          masterMachineData={getMasterMachineData.data}
          standardMachineData={getData.data}
        />
        {/* 행(row) 수정 모달 */}
        <EditRowModal
          isOpen={showEditModal.isOpen}
          originalRowData={showEditModal.rowData}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        />
        {/* 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={updateDeleteData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
      </>
    );
  }

  // StandardMachine 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

/**
 * 설비기준정보 동기화 관련 객체 정의 및 함수
 */
// AG Grid Colums 정의
const machineListColumn = [
  {
    field: 'machine_id', // 칼럼 데이터 key
    headerName: 'Machine ID', // 칼럼 헤더 이름
    flex: 1,
    cellStyle: { textAlign: 'center' },     // 셀 내용 가로 가운데
    headerClass: 'header-center',           // 헤더 가운데 (CSS 필요)
    //headerClass: innerStyles.headerText, // 특정 칼럼에 css 적용 시 hearclass,cellclass 사용
    //cellClass: innerStyles.cellText,
    filter: true, // 필터 기능 활성화 
  },
  { 
    field: 'machine_name',
    headerName: '설비명',
    valueFormatter: (params) => { // cell 데이터 변경
      const { machine_name, safe_machine_name } = params.data;
      let name = `${safe_machine_name} (${machine_name})`
      if(!safe_machine_name || (safe_machine_name === '')) {
        name = `${machine_name}`;
      }
      return name;
    },
    flex: 1,
    cellStyle: { textAlign: 'center' },     // 셀 내용 가로 가운데
    headerClass: 'header-center',           // 헤더 가운데 (CSS 필요)
    //headerClass: innerStyles.headerText,
    //cellClass: innerStyles.cellText,
    filter: true,
  }, 
]
// 장비 정보 동기화 모달 컴포넌트
const SyncMachineListModal = ({
  isOpen,
  onConfirm = null,
  onCancel = null,
  masterMachineData = null, // 마스터 데이터
  standardMachineData = null, // 설비기준정보 데이터
}) => {
  const theme = useTheme();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("lg"));
  //const { group_code, project_code } = useParams();

  // 선택한 행의 정보
  const [selectedRow, setSelectedRow] = useState([]);

  // ag grid 정보
  const gridRef = useRef();
  const clearSelection = () => {
    // ag grid의 선택한 행 초기화
    gridRef.current.api.deselectAll();
  };

  // 장비 목록 동기화 테이블의 선택 유/무 판단
  const handleSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    if(selected.length > 0) {
      setSelectedRow(selected);
      //console.log('선택된 행:', selected);
    }
    else {
      setSelectedRow([]);
    }
  };

  // 장비 동기화 처리
  const handleSyncMachine = async () => {
    //console.log(selectedRow);

    // 데이터 포맷 변경
    const datas = selectedRow.map(item => {
      return item.machine_id;
    });

    onConfirm(datas);
    clearSelection();
    setSelectedRow([]);
  }

  // SyncMachineList 렌더링
  return (
    <Modal 
      open={isOpen}
      onClose={onCancel}
    >
      {/* 컨테이너 */}
      <Box sx={{ 
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        boxShadow: 24,
        padding: isCompactLayout ? theme.spacing(2) : theme.spacing(4),

        width: isCompactLayout ? 'calc(100vw - 24px)' : 'auto',
        minWidth: isCompactLayout ? 0 : '25%',
        maxWidth: isCompactLayout ? 'calc(100vw - 24px)' : '90%',
        maxHeight: '90vh',  // ✅ 전체 제한
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* 타이틀 */}
        <Box 
          sx={{
            fontSize: "18px",                
            fontWeight: FONT_PROPS.MEDIUM,
            color: FONT_PROPS.DARK,
            padding: "0px 0px 20px 0px",
            borderBottom: `1px solid ${COLORS.BORDER_BRIGHT}`,
          }}
        >
          {CHILD_TITLE} 동기화
        </Box>
        {/* 설비 정보 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 2, lg: 5 },
            p: { xs: 1, lg: '12px' },
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <Typography fontSize={16} fontWeight={FONT_PROPS.MEDIUM} mb={2} mt={2} ml={{ xs: 0, lg: 4 }}>
              {CHILD_TITLE}
            </Typography>
            <Box
              className="ag-theme-quartz-machine"
              sx={{ height: { xs: 280, sm: 340, lg: 500 }, width: '100%', minWidth: { xs: 0, lg: 550 } }}
            >
              <AgGridReact
                rowData={standardMachineData} // 데이터
                columnDefs={machineListColumn} // 데이터 칼럼 정의
                pagination={true} // 페이지 네이션 사용 유무
                //paginationPageSize={100} // 페이지 네이션 선택 사이즈
                //paginationPageSizeSelector={[5, 10, 20, 50, 100]} // 페이지 네이션 사이즈 설정
                //paginationPageSizeSelector={false} // ✅ 드롭다운 비활성화
                localeText={AG_GRID_LOCALE_KR} // 👈 한글 적용
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <Typography fontSize={16} fontWeight={FONT_PROPS.MEDIUM} mb={2} mt={2} ml={{ xs: 0, lg: 4 }}>
              설비기준정보(마스터데이터)
            </Typography>
            <Box
              className="ag-theme-quartz-machine"
              sx={{ height: { xs: 280, sm: 340, lg: 500 }, width: '100%', minWidth: { xs: 0, lg: 550 } }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={masterMachineData} // 데이터
                columnDefs={machineListColumn} // 데이터 칼럼 정의
                pagination={true} // 페이지 네이션 사용 유무
                //paginationPageSize={100} // 페이지 네이션 선택 사이즈
                //paginationPageSizeSelector={[5, 10, 20, 50, 100]} // 페이지 네이션 사이즈 설정
                //paginationPageSizeSelector={false} // ✅ 드롭다운 비활성화
                localeText={AG_GRID_LOCALE_KR} // 👈 한글 적용
                rowSelection={{
                  mode: 'multiRow', // ✅ v33에서 'multiple' 대신 사용
                  //allowMultipleRanges: true, // 선택적으로 사용 가능
                }}
                onSelectionChanged={handleSelectionChanged}
              />
            </Box>
          </Box>
        </Box>
        {/* 버튼 */}
        <Box 
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${COLORS.BORDER_BRIGHT}`,
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        > 
          <Button 
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleSyncMachine}
            disabled = {selectedRow.length === 0}
            fullWidth={isCompactLayout}
          >
            가져오기
          </Button>
          <Button 
            variant="outlined" 
            onClick={onCancel}
            fullWidth={isCompactLayout}
          >
            닫기
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

/////////////////////////////////////

// 테이블의 행(row) 수정 모달 컴포넌트
const EditRowModal = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(()=>{
    if(!originalRowData) {
      return;
    }
    const tempData = { ...originalRowData };
    tempData['machine_purchase_date'] = tempData['machine_purchase_date'] ? dayjs(tempData['machine_purchase_date']) : null;
    setEditData(tempData);
  },[originalRowData]);

  // 행 수정
  const handleEditRowConfirm = () => {
    // 데이터 포맷 변경
    const data = { 
      ...editData, 
      machine_purchase_date: transFormatDateFromDayjs(editData.machine_purchase_date),
    };
    onConfirm(data);
    clearEditData();
  };

  // 날짜 데이터 포맷 변경 (dayjs -> date)
  const transFormatDateFromDayjs = (value) => {
    if (!value) return null;
    const dayjsValue = dayjs(value);
    if (!dayjsValue.isValid()) return null;
    
    return dayjsValue.format("YYYY-MM-DD HH:mm:ss");
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleEditRowConfirm}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{
        width: { xs: 'calc(100vw - 24px)', sm: 640, lg: 400 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: '90vh',
      }}
      contentSX={{
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, // 칼럼 2개
        columnGap: '20px',
        rowGap: { xs: '4px', sm: '0px' },
        overflow: 'auto',
      }}
    >
      {/* 설비관리번호 */}
      <TextField 
        label={'설비관리번호'}
        value={editData?.machine_manage_code || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_manage_code: e.target.value,
          });
        }}
      />
      {/* 설비명 */}
      <TextField 
          label={'설비명'}
          value={editData?.safe_machine_name || editData?.machine_name || ""}
          type="text"
          fullWidth
          margin="normal"
          onChange={(e) => {
            setEditData({
              ...editData,
              safe_machine_name: e.target.value,
            });
          }}
      />
      {/* 제작사 */}
      <TextField 
        label={'제작사'}
        value={editData?.machine_manufacturer || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_manufacturer: e.target.value,
          });
        }}
      />
      {/* 모델명 */}
      <TextField 
        label={'모델명'}
        value={editData?.machine_model_name || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_model_name: e.target.value,
          });
        }}
      />
      {/* 규격 */}
      <TextField 
        label={'규격'}
        value={editData?.machine_standard || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_standard: e.target.value,
          });
        }}
      />
      {/* 구입일자 */}
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ko"
        //key={column.accessorKey}
      >
        <DatePicker
          label={'구입일자'}
          value={editData?.machine_purchase_date || null} // data 관리는 객체로 해야 함
          onChange={(e) => {
            setEditData({
              ...editData,
              machine_purchase_date: e,
            });
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                marginTop: '16px',
                marginBottom: '8px',
              },
            },
          }}
        />
      </LocalizationProvider>
      {/* 내용연수 */}
      <TextField 
        label={'내용연수'}
        value={editData?.machine_age || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_age: e.target.value,
          });
        }}
      />
      {/* 설비 size */}
      <TextField 
        label={'설비 SIZE'}
        value={editData?.machine_size || ""}
        type="text"
        fullWidth
        margin="normal"
        onChange={(e) => {
          setEditData({
            ...editData,
            machine_size: e.target.value,
          });
        }}
      />
      {/* 전압 */}
      <TextField 
          label={'전압'}
          value={editData?.machine_voltage || ""}
          type="text"
          fullWidth
          margin="normal"
          onChange={(e) => {
            setEditData({
              ...editData,
              machine_voltage: e.target.value,
            });
          }}
      />
    </ModalTemplate>
  );
}
